"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const vectorFields_1 = require("./vectorFields");
const VectorFieldVisualization = () => {
    const canvasRef = (0, react_1.useRef)(null);
    const animationRef = (0, react_1.useRef)(0);
    const [selectedField, setSelectedField] = (0, react_1.useState)('Gravitational Field');
    const [fieldStrength, setFieldStrength] = (0, react_1.useState)(100);
    const [showVectors, setShowVectors] = (0, react_1.useState)(true);
    const [showParticles, setShowParticles] = (0, react_1.useState)(true);
    const [showTrails, setShowTrails] = (0, react_1.useState)(true);
    const [isAnimating, setIsAnimating] = (0, react_1.useState)(false);
    const [vectorFieldData, setVectorFieldData] = (0, react_1.useState)(null);
    const fields = (0, vectorFields_1.getDefaultFields)();
    const width = 800;
    const height = 600;
    // Memoize the current field configuration to prevent infinite loops
    const currentField = react_1.default.useMemo(() => (Object.assign(Object.assign({}, fields[selectedField]), { strength: fieldStrength })), [selectedField, fieldStrength]);
    const renderVisualization = (0, react_1.useCallback)(() => {
        const canvas = canvasRef.current;
        if (!canvas || !vectorFieldData)
            return;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        // Clear canvas
        ctx.fillStyle = '#0f172a';
        ctx.fillRect(0, 0, width, height);
        // Draw vectors
        if (showVectors) {
            vectorFieldData.vectors.forEach(vector => {
                const magnitude = Math.sqrt(vector.vx * vector.vx + vector.vy * vector.vy);
                if (magnitude < 0.1)
                    return; // Skip very small vectors
                // Scale vector for visibility
                const scale = Math.min(15, 15 / Math.max(magnitude, 1));
                const endX = vector.x + vector.vx * scale;
                const endY = vector.y + vector.vy * scale;
                // Draw arrow shaft
                ctx.strokeStyle = '#6366f1';
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(vector.x, vector.y);
                ctx.lineTo(endX, endY);
                ctx.stroke();
                // Draw arrowhead
                const angle = Math.atan2(vector.vy, vector.vx);
                const arrowLength = 8;
                const arrowAngle = Math.PI / 6;
                ctx.beginPath();
                ctx.moveTo(endX, endY);
                ctx.lineTo(endX - arrowLength * Math.cos(angle - arrowAngle), endY - arrowLength * Math.sin(angle - arrowAngle));
                ctx.moveTo(endX, endY);
                ctx.lineTo(endX - arrowLength * Math.cos(angle + arrowAngle), endY - arrowLength * Math.sin(angle + arrowAngle));
                ctx.stroke();
                // Draw magnitude indicator (color intensity)
                const intensity = Math.min(1, magnitude / 50);
                const alpha = 0.3 + intensity * 0.7;
                ctx.fillStyle = `rgba(99, 102, 241, ${alpha})`;
                ctx.beginPath();
                ctx.arc(vector.x, vector.y, 2, 0, 2 * Math.PI);
                ctx.fill();
            });
        }
        // Draw particle trails
        if (showTrails && showParticles) {
            vectorFieldData.particles.forEach(particle => {
                if (particle.trail.length < 2)
                    return;
                ctx.strokeStyle = '#10b981';
                ctx.lineWidth = 1;
                ctx.beginPath();
                particle.trail.forEach((point, index) => {
                    const alpha = index / particle.trail.length;
                    ctx.strokeStyle = `rgba(16, 185, 129, ${alpha * 0.8})`;
                    ctx.lineWidth = 1 + alpha * 2;
                    if (index === 0) {
                        ctx.moveTo(point.x, point.y);
                    }
                    else {
                        ctx.lineTo(point.x, point.y);
                    }
                });
                ctx.stroke();
            });
        }
        // Draw particles
        if (showParticles) {
            vectorFieldData.particles.forEach(particle => {
                // Particle
                ctx.fillStyle = '#f59e0b';
                ctx.beginPath();
                ctx.arc(particle.x, particle.y, 3, 0, 2 * Math.PI);
                ctx.fill();
                // Velocity indicator
                const speed = Math.sqrt(particle.vx * particle.vx + particle.vy * particle.vy);
                if (speed > 0.1) {
                    const scale = Math.min(20, speed * 10);
                    const endX = particle.x + (particle.vx / speed) * scale;
                    const endY = particle.y + (particle.vy / speed) * scale;
                    ctx.strokeStyle = '#f59e0b';
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.moveTo(particle.x, particle.y);
                    ctx.lineTo(endX, endY);
                    ctx.stroke();
                }
            });
        }
        // Draw field center
        ctx.fillStyle = '#ef4444';
        ctx.beginPath();
        ctx.arc(currentField.centerX, currentField.centerY, 6, 0, 2 * Math.PI);
        ctx.fill();
        // Draw field center label
        ctx.fillStyle = '#ef4444';
        ctx.font = 'bold 12px ui-sans-serif, system-ui';
        ctx.textAlign = 'center';
        ctx.fillText('Field Center', currentField.centerX, currentField.centerY - 15);
    }, [vectorFieldData, showVectors, showParticles, showTrails, currentField.centerX, currentField.centerY]);
    const animate = (0, react_1.useCallback)(() => {
        if (!isAnimating)
            return;
        setVectorFieldData(prev => {
            if (!prev)
                return null;
            const updatedParticles = (0, vectorFields_1.updateParticles)(prev.particles, currentField, 0.5); // Increased dt for better visibility
            return Object.assign(Object.assign({}, prev), { particles: updatedParticles });
        });
        renderVisualization(); // Explicitly render after updates
        animationRef.current = requestAnimationFrame(animate);
    }, [isAnimating, currentField, renderVisualization]);
    (0, react_1.useEffect)(() => {
        if (isAnimating) {
            animationRef.current = requestAnimationFrame(animate);
        }
        else {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        }
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [isAnimating, animate]);
    (0, react_1.useEffect)(() => {
        const newData = (0, vectorFields_1.generateVectorField)(width, height, currentField, 25);
        setVectorFieldData(newData);
    }, [currentField]);
    // Removed self-referential useEffect
    const toggleAnimation = () => {
        setIsAnimating(!isAnimating);
    };
    const resetParticles = () => {
        const newData = (0, vectorFields_1.generateVectorField)(width, height, currentField, 25);
        setVectorFieldData(newData);
    };
    const handleCanvasClick = (0, react_1.useCallback)((e) => {
        var _a;
        const rect = (_a = canvasRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        if (!rect)
            return;
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        // Update field center
        const newField = Object.assign(Object.assign({}, currentField), { centerX: x, centerY: y });
        const newData = (0, vectorFields_1.generateVectorField)(width, height, newField, 25);
        setVectorFieldData(newData);
    }, [currentField]);
    return (react_1.default.createElement("div", { className: "vector-field-visualization" },
        react_1.default.createElement("div", { className: "visualization-header" },
            react_1.default.createElement("h1", null, "Vector Fields and Flow Lines"),
            react_1.default.createElement("p", null, "Explore different types of vector fields and watch particles follow the flow lines. Click anywhere to move the field center, and observe how particles respond to the changing field.")),
        react_1.default.createElement("div", { className: "controls" },
            react_1.default.createElement("div", { className: "control-group" },
                react_1.default.createElement("label", { htmlFor: "field" }, "Field Type:"),
                react_1.default.createElement("select", { id: "field", value: selectedField, onChange: (e) => setSelectedField(e.target.value) }, Object.keys(fields).map(name => (react_1.default.createElement("option", { key: name, value: name }, name))))),
            react_1.default.createElement("div", { className: "control-group" },
                react_1.default.createElement("label", { htmlFor: "strength" }, "Field Strength:"),
                react_1.default.createElement("input", { id: "strength", type: "range", min: "10", max: "500", step: "10", value: fieldStrength, onChange: (e) => setFieldStrength(parseInt(e.target.value)) }),
                react_1.default.createElement("span", null, fieldStrength)),
            react_1.default.createElement("div", { className: "control-group" },
                react_1.default.createElement("label", null,
                    react_1.default.createElement("input", { type: "checkbox", checked: showVectors, onChange: (e) => setShowVectors(e.target.checked) }),
                    "Show Vectors")),
            react_1.default.createElement("div", { className: "control-group" },
                react_1.default.createElement("label", null,
                    react_1.default.createElement("input", { type: "checkbox", checked: showParticles, onChange: (e) => setShowParticles(e.target.checked) }),
                    "Show Particles")),
            react_1.default.createElement("div", { className: "control-group" },
                react_1.default.createElement("label", null,
                    react_1.default.createElement("input", { type: "checkbox", checked: showTrails, onChange: (e) => setShowTrails(e.target.checked) }),
                    "Show Trails")),
            react_1.default.createElement("button", { onClick: toggleAnimation, className: `animate-button ${isAnimating ? 'active' : ''}` }, isAnimating ? 'Stop Animation' : 'Start Animation'),
            react_1.default.createElement("button", { onClick: resetParticles, className: "reset-button" }, "Reset Particles")),
        react_1.default.createElement("div", { className: "canvas-container" },
            react_1.default.createElement("canvas", { ref: canvasRef, width: width, height: height, onClick: handleCanvasClick, style: {
                    border: '1px solid #333',
                    borderRadius: '8px',
                    background: '#0f172a',
                    cursor: 'crosshair'
                } })),
        react_1.default.createElement("div", { className: "field-info" },
            react_1.default.createElement("h3", null, "Field Information"),
            react_1.default.createElement("p", null, (0, vectorFields_1.getFieldDescription)(currentField)),
            react_1.default.createElement("p", null,
                react_1.default.createElement("strong", null, "Instructions:"),
                " Click anywhere on the canvas to move the field center. Use the controls to adjust field parameters and toggle visualization elements.")),
        react_1.default.createElement("div", { className: "info-panel" },
            react_1.default.createElement("h3", null, "About Vector Fields"),
            react_1.default.createElement("p", null, "Vector fields assign a vector (magnitude and direction) to each point in space. They are fundamental in physics for describing forces, fluid flow, and electromagnetic fields."),
            react_1.default.createElement("p", null,
                react_1.default.createElement("strong", null, "Field Types:")),
            react_1.default.createElement("ul", null,
                react_1.default.createElement("li", null,
                    react_1.default.createElement("strong", null, "Gravitational:"),
                    " Attractive force proportional to mass and inversely proportional to distance squared"),
                react_1.default.createElement("li", null,
                    react_1.default.createElement("strong", null, "Electric:"),
                    " Force between charged particles, similar to gravity but can be attractive or repulsive"),
                react_1.default.createElement("li", null,
                    react_1.default.createElement("strong", null, "Gradient:"),
                    " Points in the direction of steepest increase of a scalar function"),
                react_1.default.createElement("li", null,
                    react_1.default.createElement("strong", null, "Vortex:"),
                    " Circular flow pattern, common in fluid dynamics"),
                react_1.default.createElement("li", null,
                    react_1.default.createElement("strong", null, "Sink/Source:"),
                    " Radial flow patterns, inward or outward respectively")),
            react_1.default.createElement("p", null, "The particle trails show the flow lines of the vector field, demonstrating how objects would move under the influence of these forces."))));
};
exports.default = VectorFieldVisualization;
